import { tenantConfig } from '../config/tenant'
import Nav from '../components/Nav'
import Seo from '../components/Seo'
import Footer from '../components/Footer'

/**
 * HardshipPage — Financial Difficulty & Hardship Hub (footer-only, regulatory).
 *
 * Required under the National Consumer Credit Protection Act 2009 (NCCP), which
 * obliges credit providers and credit assistance providers to inform customers
 * of their hardship rights and how to apply. Copy is a launch-ready draft
 * adapted for Chelston Alvares' sign-off — Greystone can tailor the wording to
 * match how their team actually handles hardship applications.
 */

const RIGHTS = [
  'You can ask your lender to change your loan terms if you are having trouble meeting repayments, this is called a hardship variation.',
  'A change might mean a short repayment pause, a reduced repayment amount, extending the loan term, or a combination that gives you breathing room.',
  'Your lender must consider a genuine hardship request and respond to you in writing, generally within 21 days.',
  'Asking for hardship assistance does not, on its own, affect your credit report, though missed repayments before you ask may.',
]

const STEPS = [
  {
    n: '1',
    title: 'Talk to us first',
    body: 'Contact your Greystone broker as early as possible, the sooner we know, the more options we can put on the table before arrears build up.',
  },
  {
    n: '2',
    title: 'Tell us what changed',
    body: 'Let us know the reason (illness, job loss, reduced income, family circumstances) and whether it is temporary or ongoing. No documents are needed to start the conversation.',
  },
  {
    n: '3',
    title: 'We lodge your hardship request',
    body: 'We help you apply to your lender for a hardship variation and advocate on your behalf, then confirm the lender\'s decision with you in writing.',
  },
]

export default function HardshipPage() {
  return (
    <>
      <Seo
        title="Financial Hardship Assistance"
        path="/hardship"
        description="If you're struggling to meet your repayments, here's how to apply for hardship assistance and what support is available."
      />
      <Nav />
      <main id="main" className="pt-36 pb-20">
        {/* HERO */}
        <section className="max-w-4xl mx-auto px-6 pb-12">
          <div className="inline-flex items-center gap-2 border border-[#E8E8E2] rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider text-gs-text-soft mb-6">
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: tenantConfig.accentColor }} />
            Regulatory · Financial difficulty
          </div>
          <h1 className="font-heading text-3xl md:text-5xl text-ink font-semibold leading-tight mb-4">
            Financial difficulty &amp; hardship support
          </h1>
          <p className="text-lg text-gs-text-soft">
            If you are struggling to keep up with your home loan or other credit repayments, you are
            not alone and you have rights. Under the National Consumer Credit Protection Act 2009
            (NCCP), you can ask for help before things get worse. {tenantConfig.name} is here to guide
            you through it, confidentially and without judgement.
          </p>
        </section>

        {/* YOUR RIGHTS */}
        <section className="max-w-4xl mx-auto px-6 pb-12">
          <h2 className="font-heading text-2xl md:text-3xl text-ink mb-6">Your hardship rights</h2>
          <ul className="space-y-4">
            {RIGHTS.map((r) => (
              <li key={r} className="flex gap-3 text-gs-text-soft">
                <span className="mt-2.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: tenantConfig.accentColor }} />
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* HOW TO APPLY */}
        <section className="max-w-4xl mx-auto px-6 pb-12">
          <h2 className="font-heading text-2xl md:text-3xl text-ink mb-6">How to apply for hardship assistance</h2>
          <div className="grid gap-4 md:grid-cols-3">
            {STEPS.map((s) => (
              <div key={s.n} className="p-6 rounded-2xl border border-[#E8E8E2] bg-white">
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center font-heading font-bold mb-3"
                  style={{ background: tenantConfig.accentColor, color: tenantConfig.primaryColor }}
                >
                  {s.n}
                </div>
                <h3 className="font-heading font-semibold text-ink mb-2">{s.title}</h3>
                <p className="text-sm text-gs-text-soft">{s.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CONTACT ROUTING */}
        <section className="max-w-4xl mx-auto px-6 pb-12">
          <div className="rounded-3xl p-8 md:p-10" style={{ background: tenantConfig.primaryColor }}>
            <h2 className="font-heading text-2xl md:text-3xl mb-3" style={{ color: tenantConfig.accentColor }}>
              Contact our hardship team
            </h2>
            <p className="text-white/80 mb-6 max-w-2xl">
              Reach out to apply for hardship assistance or simply to talk through your options. We
              will respond promptly and treat everything you share in confidence.
            </p>
            <dl className="grid gap-4 sm:grid-cols-3 text-sm">
              <div>
                <dt className="text-white/50 uppercase tracking-wide text-xs mb-1">Phone</dt>
                <dd><a href="tel:0391910274" className="text-white hover:opacity-80">(03) 9191 0274</a></dd>
              </div>
              <div>
                <dt className="text-white/50 uppercase tracking-wide text-xs mb-1">Email</dt>
                <dd><a href="mailto:enquiries@greystonefinance.com.au" className="text-white hover:opacity-80 break-words">enquiries@greystonefinance.com.au</a></dd>
              </div>
              <div>
                <dt className="text-white/50 uppercase tracking-wide text-xs mb-1">Post</dt>
                <dd className="text-white/90">811/1 Queens Road, Melbourne VIC 3004</dd>
              </div>
            </dl>
          </div>
        </section>

        {/* NATIONAL SUPPORT */}
        <section className="max-w-4xl mx-auto px-6 pb-8">
          <p className="text-sm text-gs-text-soft">
            You can also access free, independent help from the National Debt Helpline on{' '}
            <a href="tel:1800007007" className="text-ink underline decoration-1 underline-offset-2">1800 007 007</a>,{' '}
            a not-for-profit financial counselling service available Monday to Friday.
          </p>
        </section>

        {/* DISCLAIMER */}
        <section className="max-w-3xl mx-auto px-6">
          <p className="text-xs text-gs-text-muted text-center">
            General information only. Not personal financial or legal advice. Your rights and available
            options may vary by lender and by loan.
          </p>
        </section>
      </main>
      <Footer />
    </>
  )
}
